import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useUserMode } from '@/stores/userModeStore';
import { createAuthData, logAuthEvent } from '@/config/authConfig';
import { isRecoveryFlagSet } from '@/lib/recovery';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'manager' | 'staff'; // Updated to reflect new role constraint
  is_active: boolean;
  firstName?: string | null;
  lastName?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  isAdmin: boolean;
  isManager: boolean;
  isRecoverySession: boolean;
  clearRecoverySession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecoverySession, setIsRecoverySession] = useState(false);
  const { initializeModeForUser } = useUserMode();

  const fetchProfile = async (userId: string) => {
    try {
      // First get the profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return null;
      }

      // Then try to get employee data for first/last name
      const { data: employeeData } = await supabase
        .from('employees')
        .select('first_name, last_name')
        .eq('email', profileData.email)
        .single();

      const profile: Profile = {
        ...profileData as Profile,
        firstName: employeeData?.first_name || null,
        lastName: employeeData?.last_name || null
      };

      // Initialize UI mode based on server-verified role
      initializeModeForUser(profile.role);

      return profile;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  useEffect(() => {
    // Check recovery flag on mount and set up listener
    setIsRecoverySession(isRecoveryFlagSet());
    
    // Listen for localStorage changes (in case recovery flag is set in another component)
    const checkRecoveryFlag = () => {
      setIsRecoverySession(isRecoveryFlagSet());
    };
    
    window.addEventListener('storage', checkRecoveryFlag);
    
    return () => {
      window.removeEventListener('storage', checkRecoveryFlag);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      console.log('🚀 Initializing AuthProvider...');
      
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        console.log('📋 Session check result:', !!session, error?.message);
        
        if (session && !error) {
          console.log('✅ Restored existing Supabase session');
          setSession(session);
          setUser(session.user);
          setLoading(false);
        } else {
          console.log('❌ No valid session found');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error checking session:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔄 Auth state changed:', event, !!session);
        
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // Auth state changed - let components handle navigation
      }
    );

    // Initialize auth state
    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Separate effect for profile fetching - blocked during recovery
  useEffect(() => {
    if (!session?.user || isRecoverySession) {
      setProfile(null);
      return;
    }

    let isMounted = true;
    
    const loadProfile = async () => {
      console.log('👤 Fetching user profile...');
      const userProfile = await fetchProfile(session.user.id);
      if (isMounted) {
        setProfile(userProfile);
      }
    };
    
    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [session?.user, isRecoverySession, initializeModeForUser]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName
        }
      }
    });
    return { error };
  };

  const signOut = async () => {
    console.log('Signing out - clearing all auth states');
    
    // Clear Supabase auth
    await supabase.auth.signOut();
    
    // Clear SMS authentication tokens
    localStorage.removeItem('staff_sms_auth');
    localStorage.removeItem('sms_admin_auth');
    localStorage.removeItem('location_bypass');
    
    // Navigate to auth page instead of reloading
    window.location.href = '/auth';
  };

  const resetPassword = async (email: string) => {
    // HARDENED: Always use production domain for reset emails
    const redirectUrl = "https://iflsh.com/reset-password";
    
    console.log('🔑 Password reset requested for:', email);
    console.log('🔗 Production redirect URL:', redirectUrl);
    console.log('🌐 Current hostname:', window.location.hostname);
    console.log('🌐 Current protocol:', window.location.protocol);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });
    
    if (error) {
      console.error('❌ Password reset error:', error);
    } else {
      console.log('✅ Password reset email sent successfully');
      console.log('📧 Email will contain link to:', redirectUrl);
    }
    
    return { error };
  };

  const clearRecoverySession = async () => {
    console.log('🔒 Clearing recovery session and signing out');
    setIsRecoverySession(false);
    await supabase.auth.signOut();
  };

  // Role-based access - ONLY from server-verified profile
  const isAdmin = Boolean(profile?.role === 'admin');
  const isManager = Boolean(profile?.role === 'manager' || isAdmin);

  const value = {
    user,
    session,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    isAdmin,
    isManager,
    isRecoverySession,
    clearRecoverySession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
